import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Loader2,
  MessageCircle,
  RefreshCw,
  Smartphone,
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  buildWhatsappLink,
  formatBrazilPhone,
  formatBrazilPhoneInput,
  normalizeBrazilPhoneForWhatsapp,
} from '../../../../utils/whatsapp';
import { ensureActiveSession } from '../../../../services/session';
import { changeWhatsAppPhone, connectWhatsApp, fetchZapiQrCode, fetchZapiStatus } from '../services/api.ts';
import { QR_CODE_EXPIRATION_MS, ZAPI_STATUS_POLL_INTERVAL_MS } from '../constants/timing.js';
import {
  isAtendabotWhatsAppUnavailable,
  isAtendabotWhatsAppUnavailableError,
  notifyAtendabotWhatsAppStatus,
  type AtendabotWhatsAppStatus,
  WHATSAPP_INSTANCE_UNAVAILABLE_MESSAGE,
} from '../utils/whatsappStatus.ts';

type LoadingStage = 'status' | 'qrcode' | null;

export type WhatsAppSetupDraft = {
  phoneNumber: string;
  confirmed: boolean;
  ready: boolean;
};

const WhatsAppConnection = ({
  initialStatus,
  mode = 'setup',
  onStatusChange,
  onSetupDraftChange,
  initialSetupDraft,
  onOpenQrCode,
  compact = false,
  standalone = false,
}: {
  initialStatus?: AtendabotWhatsAppStatus | null;
  mode?: 'setup' | 'management';
  onStatusChange?: (status: AtendabotWhatsAppStatus) => void;
  onSetupDraftChange?: (draft: WhatsAppSetupDraft) => void;
  initialSetupDraft?: WhatsAppSetupDraft;
  onOpenQrCode?: (phoneNumber?: string | null) => void;
  compact?: boolean;
  standalone?: boolean;
}) => {
  const [status, setStatus] = useState<AtendabotWhatsAppStatus | null>(initialStatus || null);
  const [phone, setPhone] = useState(() => formatBrazilPhoneInput(initialSetupDraft?.phoneNumber || ''));
  const phoneEdited = useRef(false);
  const [qrImage, setQrImage] = useState<string | null>(null);
  const [loadingStage, setLoadingStage] = useState<LoadingStage>('status');
  const [confirmedPhone, setConfirmedPhone] = useState(initialSetupDraft?.confirmed === true);
  const [phoneChangeOpen, setPhoneChangeOpen] = useState(false);
  const [replacementPhone, setReplacementPhone] = useState('');
  const [replacementConfirmed, setReplacementConfirmed] = useState(false);
  const [changingPhone, setChangingPhone] = useState(false);
  const [creatingConnection, setCreatingConnection] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [qrExpired, setQrExpired] = useState(false);
  const normalizedPhone = useMemo(() => normalizeBrazilPhoneForWhatsapp(phone), [phone]);
  const normalizedReplacementPhone = useMemo(
    () => normalizeBrazilPhoneForWhatsapp(replacementPhone),
    [replacementPhone],
  );
  const configured = status?.configured === true;
  const connected = configured && status?.connected === true && status?.smartphoneConnected !== false;
  const instanceUnavailable = isAtendabotWhatsAppUnavailable(status);
  const lockedPhone = status?.phone_number || normalizedPhone;
  const normalizedLockedPhone = useMemo(
    () => normalizeBrazilPhoneForWhatsapp(lockedPhone || ''),
    [lockedPhone],
  );
  const replacementDiffers = Boolean(
    normalizedReplacementPhone && normalizedReplacementPhone !== normalizedLockedPhone,
  );
  const formattedLockedPhone = formatBrazilPhone(lockedPhone);
  const conversationUrl = connected ? buildWhatsappLink(lockedPhone) : '';
  const isBusy = loadingStage !== null || changingPhone || creatingConnection;
  const canChangePhone = configured
    && !instanceUnavailable
    && status?.phone_change_allowed === true;

  const applyStatusPhone = useCallback((nextStatus: AtendabotWhatsAppStatus) => {
    if (nextStatus?.configured && nextStatus.phone_number) {
      setPhone(formatBrazilPhoneInput(nextStatus.phone_number));
      return;
    }
    const suggestion = normalizeBrazilPhoneForWhatsapp(nextStatus?.suggested_phone_number || '');
    if (!suggestion) return;
    setPhone((current) => (current || phoneEdited.current ? current : formatBrazilPhoneInput(suggestion)));
  }, []);

  useEffect(() => {
    if (!status) return;
    onStatusChange?.(status);
    notifyAtendabotWhatsAppStatus(status);
  }, [onStatusChange, status]);

  useEffect(() => {
    if (configured || !onSetupDraftChange) return;
    onSetupDraftChange({
      phoneNumber: normalizedPhone,
      confirmed: confirmedPhone,
      ready: Boolean(normalizedPhone && confirmedPhone),
    });
  }, [configured, confirmedPhone, normalizedPhone, onSetupDraftChange]);

  const loadQrCode = async () => {
    setLoadingStage('qrcode');
    setQrExpired(false);
    try {
      const qr = await fetchZapiQrCode();
      setQrImage(qr?.image || null);
      if (!qr?.image) toast.error('Não foi possível obter a imagem do QR Code.');
    } catch (error: any) {
      setQrImage(null);
      if (isAtendabotWhatsAppUnavailableError(error)) {
        setStatus((current) => ({
          ...(current || {}),
          configured: true,
          connected: false,
          smartphoneConnected: false,
          instance_unavailable: true,
          error: 'instance_unavailable',
        }));
        toast.error(WHATSAPP_INSTANCE_UNAVAILABLE_MESSAGE);
      } else {
        toast.error(error?.response?.data?.error || 'Não foi possível gerar o QR Code agora.');
      }
    } finally {
      setLoadingStage(null);
    }
  };

  const createConnection = async () => {
    if (!normalizedPhone || !confirmedPhone || isBusy) return;
    setCreatingConnection(true);
    setConnectionError(null);
    try {
      const created = await connectWhatsApp(normalizedPhone);
      const alreadyConnected = created.status === 'conectada';
      const nextStatus: AtendabotWhatsAppStatus = {
        configured: true,
        connected: alreadyConnected,
        smartphoneConnected: alreadyConnected,
        status: created.status,
        phone_number: created.phone_number || normalizedPhone,
        trial_expires_at: created.trial_expires_at,
        is_subscribed: created.is_subscribed,
      };
      setStatus(nextStatus);
      try {
        await ensureActiveSession({ force: true });
      } catch (_) {
        // A sessão será atualizada após a confirmação da conexão.
      }
      if (alreadyConnected) setQrImage(null);
      else await loadQrCode();
    } catch (error: any) {
      setConnectionError(error?.response?.data?.error || error?.response?.data?.message || 'Não foi possível criar a conexão agora.');
    } finally {
      setCreatingConnection(false);
    }
  };

  const loadStatus = async ({ showLoader = true, loadQr = true } = {}) => {
    if (showLoader) setLoadingStage('status');
    try {
      const data = await fetchZapiStatus();
      setStatus(data);
      applyStatusPhone(data);
      if (data?.connected && data?.smartphoneConnected !== false) {
        setQrImage(null);
        await ensureActiveSession({ force: true });
      } else if (
        data?.configured
        && !isAtendabotWhatsAppUnavailable(data)
        && loadQr
        && mode !== 'management'
      ) {
        await loadQrCode();
        return;
      }
    } catch {
      toast.error('Não foi possível consultar a conexão agora.');
    } finally {
      if (showLoader) setLoadingStage(null);
    }
  };

  const submitPhoneChange = async () => {
    if (!replacementDiffers || !replacementConfirmed || changingPhone) return;
    setChangingPhone(true);
    try {
      const nextStatus = await changeWhatsAppPhone(normalizedReplacementPhone);
      setStatus(nextStatus);
      setPhone(formatBrazilPhoneInput(nextStatus.phone_number));
      setPhoneChangeOpen(false);
      setReplacementPhone('');
      setReplacementConfirmed(false);
      toast.success('Número atualizado. Conecte o novo WhatsApp pelo QR Code.');
      if (onOpenQrCode) onOpenQrCode(nextStatus.phone_number);
      else await loadQrCode();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Não foi possível trocar o número agora.');
    } finally {
      setChangingPhone(false);
    }
  };

  useEffect(() => {
    let active = true;
    const initialLoad = async () => {
      try {
        const data = initialStatus || await fetchZapiStatus();
        if (!active) return;
        setStatus(data);
        applyStatusPhone(data);
        if (
          data?.configured
          && !(data?.connected && data?.smartphoneConnected !== false)
          && !isAtendabotWhatsAppUnavailable(data)
          && mode !== 'management'
        ) {
          setLoadingStage('qrcode');
          const qr = await fetchZapiQrCode();
          if (active) setQrImage(qr?.image || null);
        }
      } catch (error: any) {
        if (active) {
          if (isAtendabotWhatsAppUnavailableError(error)) {
            setQrImage(null);
            setStatus((current) => ({
              ...(current || {}),
              configured: true,
              connected: false,
              smartphoneConnected: false,
              instance_unavailable: true,
              error: 'instance_unavailable',
            }));
            toast.error(WHATSAPP_INSTANCE_UNAVAILABLE_MESSAGE);
          } else {
            toast.error(error?.response?.data?.error || 'Não foi possível consultar a conexão agora.');
          }
        }
      } finally {
        if (active) setLoadingStage(null);
      }
    };
    initialLoad();
    return () => { active = false; };
  }, [applyStatusPhone, initialStatus, mode]);

  useEffect(() => {
    if (!standalone || !configured || connected || instanceUnavailable) return undefined;
    let active = true;
    let polling = false;
    const intervalId = window.setInterval(async () => {
      if (polling) return;
      polling = true;
      try {
        const nextStatus = await fetchZapiStatus();
        if (!active) return;
        setStatus(nextStatus);
        if (nextStatus.connected === true && nextStatus.smartphoneConnected !== false) {
          setQrImage(null);
          await ensureActiveSession({ force: true });
        }
      } catch (_) {
        // A próxima consulta tenta novamente enquanto a página estiver aberta.
      } finally {
        polling = false;
      }
    }, ZAPI_STATUS_POLL_INTERVAL_MS);
    return () => {
      active = false;
      window.clearInterval(intervalId);
    };
  }, [standalone, configured, connected, instanceUnavailable]);

  useEffect(() => {
    if (!qrImage) return undefined;
    const timeoutId = window.setTimeout(() => {
      setQrImage(null);
      setQrExpired(true);
    }, QR_CODE_EXPIRATION_MS);
    return () => window.clearTimeout(timeoutId);
  }, [qrImage]);

  const loadingMessage = loadingStage === 'qrcode'
      ? 'Gerando o QR Code…'
      : 'Consultando sua conexão…';

  const isManagement = mode === 'management';

  return (
    <section
      id="whatsapp-connection"
      className={`relative overflow-hidden rounded-2xl border bg-white ${
        compact || isManagement ? 'border-slate-200' : standalone ? 'border-slate-200 shadow-sm' : 'border-indigo-200 shadow-sm'
      }`}
    >
      {isBusy && (
        <div className="absolute inset-0 z-20 flex min-h-64 flex-col items-center justify-center gap-3 bg-white/90 px-6 text-center backdrop-blur-sm" role="status" aria-live="polite">
          <Loader2 className="animate-spin text-indigo-600" size={36} />
          <p className="font-semibold text-slate-800">{loadingMessage}</p>
          <p className="text-sm text-slate-500">Aguarde um momento, por favor.</p>
        </div>
      )}

      {!compact && (
        <div className={`${isManagement || standalone ? 'border-b border-slate-100 bg-slate-50' : 'border-b border-indigo-100 bg-gradient-to-r from-indigo-50 to-white'} p-5 sm:p-6`}>
          <div className="flex items-start gap-3">
            <span className={`rounded-xl p-3 text-white ${standalone ? 'bg-teal-700' : 'bg-indigo-600'}`}><Smartphone size={24} /></span>
            <div>
              <p className={`text-xs font-bold uppercase tracking-[0.16em] ${standalone ? 'text-teal-700' : 'text-indigo-600'}`}>WhatsApp do atendimento</p>
              <h2 className="mt-1 text-xl font-bold text-slate-900">
                {configured ? 'Número da IA' : 'Conecte seu número ao AtendaBot'}
              </h2>
            </div>
          </div>
        </div>
      )}

      <div className={`${compact ? 'space-y-4 p-4 sm:p-5' : 'space-y-5 p-5 sm:p-6'}`}>
        {configured ? (
          <>
            {instanceUnavailable && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900" role="alert">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="mt-0.5 flex-shrink-0 text-rose-600" size={21} />
                  <div>
                    <p className="font-bold">Conexão do WhatsApp indisponível</p>
                    <p className="mt-1 leading-6">{WHATSAPP_INSTANCE_UNAVAILABLE_MESSAGE}</p>
                  </div>
                </div>
              </div>
            )}
            {!instanceUnavailable && status?.phone_mismatch && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="mt-0.5 flex-shrink-0 text-rose-600" size={21} />
                  <div>
                    <p className="font-bold">O QR Code foi lido por outro número.</p>
                    <p className="mt-1 leading-6">A conexão incorreta foi desfeita. Leia o novo QR Code usando <strong>{formattedLockedPhone}</strong>.</p>
                  </div>
                </div>
              </div>
            )}
            {!instanceUnavailable && (
              <div className={`rounded-xl border p-4 ${compact ? 'border-slate-200 bg-slate-50' : connected ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3">
                    {connected
                      ? <CheckCircle2 className="mt-0.5 flex-shrink-0 text-emerald-600" size={22} />
                      : <Clock3 className="mt-0.5 flex-shrink-0 text-amber-600" size={22} />}
                    <div>
                      <p className={`font-bold ${compact ? 'text-slate-900' : connected ? 'text-emerald-900' : 'text-amber-900'}`}>
                        {connected ? 'WhatsApp conectado' : standalone ? 'Número desconectado' : compact && isManagement ? 'Conexão pendente' : 'Aguardando leitura do QR Code'}
                      </p>
                      <p className="mt-1 text-sm text-slate-700">
                        {connected ? 'Número conectado' : 'Número'}: <strong>{formattedLockedPhone || status?.phone_number}</strong>
                      </p>
                      <p className="mt-1 text-xs text-slate-600">
                        Trocar o número mantém a conexão atual e não altera o prazo do trial ou da assinatura da ferramenta.
                      </p>
                    </div>
                  </div>
                  {(compact || standalone) && canChangePhone && !phoneChangeOpen && (
                    <button
                      type="button"
                      onClick={() => {
                        setReplacementPhone('');
                        setReplacementConfirmed(false);
                        setPhoneChangeOpen(true);
                      }}
                      disabled={isBusy}
                      className={`inline-flex min-h-10 items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-semibold underline-offset-2 hover:bg-white/70 hover:underline disabled:opacity-60 ${standalone ? 'text-teal-700' : 'text-slate-700'}`}
                    >
                      {standalone && <Smartphone size={16} aria-hidden="true" />}
                      {standalone ? 'Trocar número de telefone' : 'Trocar número'}
                    </button>
                  )}
                </div>
              </div>
            )}

            {!compact && !standalone && canChangePhone && !phoneChangeOpen && (
              <button
                type="button"
                onClick={() => {
                  setReplacementPhone('');
                  setReplacementConfirmed(false);
                  setPhoneChangeOpen(true);
                }}
                disabled={isBusy}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-indigo-200 bg-white px-4 py-3 text-sm font-semibold text-indigo-700 hover:bg-indigo-50 disabled:opacity-60"
              >
                <Smartphone size={17} /> Trocar número de telefone
              </button>
            )}

            {canChangePhone && phoneChangeOpen && (
              <div className="space-y-4 rounded-xl border border-indigo-200 bg-indigo-50/50 p-4">
                <div>
                  <p className="font-bold text-slate-900">Trocar o número conectado</p>
                  <p className="mt-1 text-sm leading-6 text-slate-600">
                    O aparelho atual será desconectado.
                  </p>
                </div>
                <label className="block text-sm font-semibold text-slate-700" htmlFor="atendabot-replacement-phone">
                  Novo número brasileiro com DDD
                  <div className="mt-2 flex overflow-hidden rounded-lg border border-slate-300 bg-white focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-100">
                    <span className="flex items-center border-r border-slate-200 bg-slate-50 px-3 font-semibold text-slate-600">+55</span>
                    <input
                      id="atendabot-replacement-phone"
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel-national"
                      value={replacementPhone}
                      onChange={(event) => {
                        setReplacementPhone(formatBrazilPhoneInput(event.target.value));
                        setReplacementConfirmed(false);
                      }}
                      placeholder="(51) 99999-9999"
                      aria-invalid={Boolean(replacementPhone) && !normalizedReplacementPhone}
                      className="min-w-0 flex-1 px-4 py-3 font-normal outline-none"
                    />
                  </div>
                  {replacementPhone && !normalizedReplacementPhone && (
                    <span className="mt-1 block text-xs font-medium text-rose-600">Informe um DDD e um número brasileiro válidos.</span>
                  )}
                  {normalizedReplacementPhone && !replacementDiffers && (
                    <span className="mt-1 block text-xs font-medium text-amber-700">Informe um número diferente do atual.</span>
                  )}
                </label>
                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={replacementConfirmed}
                    onChange={(event) => setReplacementConfirmed(event.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-indigo-600"
                  />
                  <span>Revisei o novo número e entendo que precisarei conectá-lo usando um novo QR Code.</span>
                </label>
                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={() => setPhoneChangeOpen(false)}
                    disabled={changingPhone}
                    className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-60"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={submitPhoneChange}
                    disabled={!replacementDiffers || !replacementConfirmed || changingPhone}
                    className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {changingPhone ? 'Atualizando número...' : 'Confirmar troca e gerar QR Code'}
                  </button>
                </div>
              </div>
            )}

            {!instanceUnavailable && !connected && qrImage && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 text-center">
                <p className="font-semibold text-slate-800">
                  {isManagement ? 'Reconecte o WhatsApp da IA' : 'Escaneie o QR Code com o número informado'}
                </p>
                <p className="mt-1 text-sm text-slate-500">WhatsApp → Aparelhos conectados → Conectar um aparelho.</p>
                <img
                  src={qrImage.startsWith('data:image') ? qrImage : `data:image/png;base64,${qrImage}`}
                  alt="QR Code para conectar o WhatsApp à IA"
                  className="mx-auto mt-4 h-56 w-56 rounded-xl border border-slate-200 bg-white p-3"
                />
                <button type="button" onClick={() => loadQrCode()} disabled={isBusy} className="mt-4 inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60">
                  <RefreshCw size={16} /> Gerar novo QR Code
                </button>
              </div>
            )}

            <div className={compact ? 'flex flex-col gap-3 sm:flex-row sm:items-center' : standalone ? 'flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end' : 'space-y-5'}>
              {!instanceUnavailable && !connected && isManagement && (
                <button
                  type="button"
                  onClick={() => onOpenQrCode ? onOpenQrCode(status?.phone_number || lockedPhone) : loadQrCode()}
                  disabled={isBusy}
                  className={compact ? 'inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-teal-700 px-5 font-semibold text-white hover:bg-teal-800 disabled:opacity-60' : standalone ? 'inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-teal-700 px-5 font-semibold text-white hover:bg-teal-800 disabled:opacity-60 sm:w-auto' : 'inline-flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-3 font-semibold text-white hover:bg-indigo-700 disabled:opacity-60'}
                >
                  <RefreshCw size={17} /> Gerar QR Code
                </button>
              )}
              {!instanceUnavailable && !connected && !isManagement && !qrImage && (
                <button type="button" onClick={() => loadQrCode()} disabled={isBusy} className={compact ? 'inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-teal-700 px-5 font-semibold text-white hover:bg-teal-800 disabled:opacity-60' : standalone ? 'inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-teal-700 px-5 font-semibold text-white hover:bg-teal-800 disabled:opacity-60 sm:w-auto' : 'inline-flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-3 font-semibold text-white hover:bg-indigo-700 disabled:opacity-60'}>
                  <RefreshCw size={17} /> {qrExpired ? 'Gerar novo QR Code' : 'Gerar QR Code'}
                </button>
              )}
              {connected && conversationUrl && (
                <a href={conversationUrl} target="_blank" rel="noopener noreferrer" className={compact ? 'inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-teal-700 px-5 font-semibold text-white transition hover:bg-teal-800' : standalone ? 'inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-teal-700 px-5 font-semibold text-white transition hover:bg-teal-800 sm:w-auto' : 'inline-flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-3 font-semibold text-white transition hover:bg-emerald-700'}>
                  <MessageCircle size={18} /> Abrir conversa com este número
                </a>
              )}
              <button type="button" onClick={() => loadStatus({ loadQr: false })} disabled={isBusy} className={compact ? 'inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60' : standalone ? 'inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60 sm:w-auto' : 'inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60'}>
                <RefreshCw size={16} /> Verificar conexão
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 flex-shrink-0 text-amber-600" size={21} />
                <div>
                  <p className="font-bold">Conecte o WhatsApp quando estiver pronto para usar o atendimento.</p>
                  <p className="mt-1 leading-6">
                    Esta etapa é opcional. Você pode configurar e testar a conversa sem cadastrar um número. A conexão usa o período de teste ou a assinatura atual da ferramenta; conectar ou trocar o número não inicia um novo trial.
                  </p>
                </div>
              </div>
            </div>

            <label className="block text-sm font-semibold text-slate-700" htmlFor="atendabot-whatsapp-phone">
              Número brasileiro com DDD (opcional)
              <div className="mt-2 flex overflow-hidden rounded-lg border border-slate-300 bg-white focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-100">
                <span className="flex items-center border-r border-slate-200 bg-slate-50 px-3 font-semibold text-slate-600">+55</span>
                <input
                  id="atendabot-whatsapp-phone"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  value={phone}
                  onChange={(event) => {
                    phoneEdited.current = true;
                    setPhone(formatBrazilPhoneInput(event.target.value));
                    setConfirmedPhone(false);
                  }}
                  placeholder="(51) 99999-9999"
                  aria-invalid={Boolean(phone) && !normalizedPhone}
                  className="min-w-0 flex-1 px-4 py-3 font-normal outline-none"
                />
              </div>
              {phone && !normalizedPhone && <span className="mt-1 block text-xs font-medium text-rose-600">Informe um DDD e um número brasileiro válidos.</span>}
              {normalizedPhone && <span className="mt-1 block text-xs text-slate-500">Será cadastrado como {formatBrazilPhone(normalizedPhone)}.</span>}
            </label>

            <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
              <input type="checkbox" checked={confirmedPhone} onChange={(event) => setConfirmedPhone(event.target.checked)} className="mt-1 h-4 w-4 rounded border-slate-300 text-indigo-600" />
              <span>Revisei o DDD e o número e entendo que a conexão usa o período de teste ou a assinatura atual da ferramenta.</span>
            </label>

            <div className={`rounded-xl border px-4 py-3 text-sm ${
              normalizedPhone && confirmedPhone
                ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                : 'border-slate-200 bg-slate-50 text-slate-600'
            }`} role="status">
              <div className="flex items-start gap-3">
                <CheckCircle2 className={`mt-0.5 flex-shrink-0 ${normalizedPhone && confirmedPhone ? 'text-emerald-600' : 'text-slate-400'}`} size={20} />
                <div>
                  <p className="font-semibold">
                    {normalizedPhone && confirmedPhone
                      ? standalone
                        ? 'Número pronto para conectar'
                        : 'Número pronto para publicação'
                      : phone
                        ? 'Confirme o número para continuar'
                        : 'Você pode continuar sem cadastrar um número'}
                  </p>
                  <p className="mt-1 leading-5">
                    {normalizedPhone && confirmedPhone
                      ? standalone
                        ? 'O cadastro e o QR Code serão gerados quando você conectar o WhatsApp.'
                        : 'A instância ainda não será criada. O cadastro e o QR Code serão gerados somente ao publicar o atendimento na última etapa.'
                      : 'As informações e o teste da conversa continuam disponíveis. Nenhuma instância será criada enquanto o número não estiver confirmado.'}
                  </p>
                </div>
              </div>
            </div>
            {standalone && (
              <div className="flex flex-col gap-3 sm:items-end">
                {connectionError && <p className="self-stretch rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800" role="alert">{connectionError}</p>}
                <button
                  type="button"
                  onClick={createConnection}
                  disabled={!normalizedPhone || !confirmedPhone || isBusy}
                  className="inline-flex min-h-11 w-full items-center justify-center rounded-lg bg-teal-700 px-5 font-semibold text-white hover:bg-teal-800 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  {creatingConnection ? 'Criando conexão...' : 'Conectar WhatsApp'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default WhatsAppConnection;
